/**
 * Chat Initialization Service - Global chat setup for the entire app
 * Handles socket connection and unread count initialization across all pages
 */

import { useAuthStore } from '@/stores/authStore'
import { useChatStore } from '@/stores/chatStore'
import { chatService } from '@/services/chatService'

class ChatInitializationService {
  private isInitialized = false
  private initializationPromise: Promise<void> | null = null

  /**
   * Initialize chat system globally
   * This should be called once when the app starts and user is authenticated
   */
  async initialize(): Promise<void> {
    // Prevent multiple simultaneous initializations
    if (this.initializationPromise) {
      return this.initializationPromise
    }

    // Skip if already initialized or user not authenticated
    const authStore = useAuthStore()
    if (this.isInitialized || !authStore.isLoggedIn) {
      return
    }

    this.initializationPromise = this.performInitialization()
    
    try {
      await this.initializationPromise
      this.isInitialized = true
    } catch (error) {
      console.error('Failed to initialize chat system:', error)
      // Reset promise so we can retry later
      this.initializationPromise = null
      throw error
    }
  }

  /**
   * Perform the actual initialization
   */
  private async performInitialization(): Promise<void> {
    const authStore = useAuthStore()
    const chatStore = useChatStore()

    // Get user's organization ID
    const currentUserOrgId = authStore.user?.org_profile?.id

    // Gracefully handle users without an organization profile (e.g. regular users, admins)
    if (!currentUserOrgId) {
      console.log('Chat initialization skipped: User has no organization profile')
      return
    }

    // Initialize socket connection first for real-time updates
    try {
      await chatStore.initializeSocket()
    } catch (error) {
      console.warn('Socket initialization failed, continuing without real-time updates:', error)
      // Don't throw - we can still load unread counts without sockets
    }

    // Load conversations and initialize unread counts
    await this.loadUnreadCounts(currentUserOrgId)
  }

  /**
   * Load unread counts for all user conversations
   */
  private async loadUnreadCounts(userOrgId: number): Promise<void> {
    try {
      // Get all unread counts efficiently in one batch operation
      const unreadCounts = await chatService.getAllUnreadCounts(userOrgId)
      
      // Update the chat store with the unread counts
      const chatStore = useChatStore()
      Object.entries(unreadCounts).forEach(([conversationId, count]) => {
        chatStore.setUnreadCount(conversationId, count)
      })

      const totalConversations = Object.keys(unreadCounts).length
      const totalUnreadCount = Object.values(unreadCounts).reduce((sum, count) => sum + count, 0)
      console.log(`Initialized unread counts for ${totalConversations} conversations (${totalUnreadCount} total unread messages)`)
    } catch (error) {
      console.error('Failed to load unread counts:', error)
      throw error
    }
  }

  /**
   * Reset initialization state (useful for logout/login scenarios)
   */
  reset(): void {
    this.isInitialized = false
    this.initializationPromise = null
  }

  /**
   * Check if chat system is initialized
   */
  get initialized(): boolean {
    return this.isInitialized
  }

  /**
   * Reinitialize if needed (e.g., after login)
   */
  async reinitializeIfNeeded(): Promise<void> {
    const authStore = useAuthStore()
    
    if (!this.isInitialized && authStore.isLoggedIn) {
      await this.initialize()
    }
  }
}

// Create singleton instance
export const chatInitializationService = new ChatInitializationService()
export default chatInitializationService